import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { SupabaseClient } from '../../lib/supabase';
import type { AccessState, Profile } from './access';
import { denialFromInviteError, resolveAccess } from './access';
import { forgetDestination } from '../routing/intendedDestination';

interface AuthContextValue {
  access: AccessState;
  /** The authenticated Supabase client, so feature data access reuses one client. */
  client: SupabaseClient;
  signInWithGoogle: () => Promise<void>;
  /** Re-consents with Google so a refresh token can be stored. */
  reconnectDrive: () => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * The Google API permissions, asked for ONLY by the explicit connect flow.
 *
 * Drive uploads run in the browser as the signed-in person, and gmail.compose
 * lets the app create a DRAFT in their own Gmail — it never sends one. But
 * gmail.compose is the permission Google labels "Manage drafts and send
 * emails", and what the app chooses to do with a permission is not what the
 * consent screen says about it.
 *
 * ── Why sign-in no longer asks for these ───────────────────────────────────
 *
 * Alex, 12 September, opening the CEO Brief on his phone and stopping at the
 * consent screen. He was reading it correctly. He was being asked to let
 * something called `anzeboaqlvquwljtdnye.supabase.co` — the raw Supabase
 * project ref, with no Privacy Policy or Terms link because the Google Cloud
 * app is unverified — send email as him, in order to read a page.
 *
 * Everyone signing in was asked for both scopes, whether they would ever
 * upload a file or open the composer or not. Most people here do neither. So
 * the permission screen was shown to everybody to serve a handful, and it was
 * shown at the worst possible moment: the first thing a new person sees.
 *
 * Sign-in now asks for nothing beyond identity. Drive and Gmail are requested
 * at the point somebody actually uses them, through reconnectDrive below, and
 * only for the person using them. Alex never sends mail from the Hub, so he
 * never sees that screen again.
 *
 * ── What this does not fix ─────────────────────────────────────────────────
 *
 * The three people who DO send mail still meet the same unverified consent
 * screen with the same project-ref name. That is a Google Cloud console job —
 * app name, logo, privacy policy and terms URLs, domain verification — plus a
 * Supabase custom auth domain, and `drive` being a restricted scope may pull
 * in a review. Worth doing, and easier now that it faces three people rather
 * than everyone.
 */
const GOOGLE_API_SCOPES =
  'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/gmail.compose';

const AuthContext = createContext<AuthContextValue | null>(null);

async function loadProfile(client: SupabaseClient, userId: string): Promise<Profile | null> {
  const { data, error } = await client.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  return data;
}

/**
 * Loads the caller's access state for a session: read own profile; when none
 * exists, attempt invitation acceptance (the only client path that can create
 * a profile), then use its result. Denies on any failure — never fails open.
 */
async function loadAccess(client: SupabaseClient, session: Session): Promise<AccessState> {
  try {
    const existing = await loadProfile(client, session.user.id);
    if (existing) {
      return resolveAccess(existing);
    }
    const { data, error } = await client.rpc('accept_invite');
    if (error) {
      return { status: 'denied', reason: denialFromInviteError(error.message) };
    }
    return resolveAccess(data);
  } catch {
    return { status: 'denied', reason: 'unknown_error' };
  }
}

export function AuthProvider({
  client,
  children,
}: {
  client: SupabaseClient;
  children: ReactNode;
}) {
  const [access, setAccess] = useState<AccessState>({ status: 'loading' });
  const lastSeenSessionRef = useRef<string | null>(null);
  // Concurrency guards. Access resolution is single-flight per session token,
  // an approved session is pinned so no later resolution can overwrite it,
  // and a monotonic sequence discards stale results.
  const sequenceRef = useRef(0);
  const inFlightTokenRef = useRef<string | null>(null);
  const approvedTokenRef = useRef<string | null>(null);

  const applySession = useCallback(
    async (session: Session | null) => {
      if (!session) {
        sequenceRef.current += 1;
        inFlightTokenRef.current = null;
        approvedTokenRef.current = null;
        lastSeenSessionRef.current = null;
        setAccess({ status: 'signed_out' });
        return;
      }

      const token = session.access_token;
      if (approvedTokenRef.current === token) {
        // Already approved for this session. A repeated event (or a failed
        // duplicate resolution) must never downgrade an approved state.
        return;
      }
      if (inFlightTokenRef.current === token) {
        // Single-flight: a resolution for this session is already running.
        return;
      }

      const requestId = ++sequenceRef.current;
      inFlightTokenRef.current = token;
      const next = await loadAccess(client, session);
      if (inFlightTokenRef.current === token) {
        inFlightTokenRef.current = null;
      }
      if (requestId !== sequenceRef.current) {
        // A newer session event superseded this request; discard the result.
        return;
      }

      if (next.status === 'approved') {
        approvedTokenRef.current = token;
      }
      setAccess(next);

      // Controlled last_seen_at update: once per session, approved users only.
      if (next.status === 'approved' && lastSeenSessionRef.current !== token) {
        lastSeenSessionRef.current = token;
        void client.rpc('touch_last_seen');
      }
    },
    [client],
  );

  useEffect(() => {
    let disposed = false;

    // Single authoritative initialization path: supabase-js emits
    // INITIAL_SESSION on subscription, so a separate getSession() bootstrap
    // is unnecessary and was the source of a duplicate-resolution race.
    let deposited = '';
    const { data: subscription } = client.auth.onAuthStateChange((event, session) => {
      if (disposed) {
        return;
      }
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'INITIAL_SESSION') {
        void applySession(session);
      }
      // Google hands over a refresh token exactly once — on the sign-in that
      // consented — and Supabase drops it from the session on the next
      // refresh. Deposit it or there is no second chance without asking the
      // person to consent again.
      //
      // Deliberately NOT gated on the event name. This first shipped checking
      // for SIGNED_IN and silently stored nothing: coming back through
      // /auth/callback is a fresh page load, so supabase-js recovers the
      // session from the URL and reports INITIAL_SESSION instead. Betting on
      // which event fires means betting on library internals. The presence of
      // a token is the real signal, so that is what is checked.
      const refreshToken = session?.provider_refresh_token;

      if (typeof refreshToken === 'string' && refreshToken !== '' && refreshToken !== deposited) {
        // Remembered so the repeated events of one page load do not each fire
        // a write. The database call is an idempotent upsert regardless.
        deposited = refreshToken;
        // .then() is not decoration. A supabase-js builder is a LAZY thenable:
        // it issues no HTTP request until something awaits it, so the earlier
        // `void client.rpc(...)` built a request and threw it away. Nothing
        // reached the network and nothing errored — the token simply never
        // arrived, which is the worst shape a bug can take.
        void client
          .rpc('store_google_refresh_token', {
            token: refreshToken,
            // Still accurate, and now for a narrower reason: a refresh token
            // is only ever issued by the reconnect flow, which is the only
            // place that asks for these. Sign-in requests no API scopes and
            // uses no access_type=offline, so it produces no refresh token to
            // mislabel.
            granted_scopes: GOOGLE_API_SCOPES,
          })
          .then(({ error }) => {
            if (error) {
              // Losing the token is silent and unrecoverable without another
              // consent, so a failure says so rather than vanishing.
              console.error('Could not store the Google refresh token', error.message);
              deposited = '';
            }
          });
      }
    });

    return () => {
      disposed = true;
      subscription.subscription.unsubscribe();
    };
  }, [client, applySession]);

  /**
   * Identity only. No `scopes` option at all, so Google is asked for nothing
   * beyond the default openid/email/profile and shows the plain account
   * chooser rather than a permissions screen. See GOOGLE_API_SCOPES above.
   */
  const signInWithGoogle = useCallback(async () => {
    const redirectTo = new URL('/auth/callback', import.meta.env['VITE_APP_URL']).href;
    const { error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) {
      setAccess({ status: 'denied', reason: 'unknown_error' });
    }
  }, [client]);

  /**
   * The explicit "Reconnect Google Drive" flow.
   *
   * Deliberately NOT part of normal sign-in. Google only issues a refresh
   * token on a fresh consent, so getting one requires prompt=consent — and
   * putting that on the everyday sign-in would show the permissions screen
   * every single time anyone signed in, forever. Here it costs one screen,
   * once, and only for people who want Drive to keep working.
   */
  const reconnectDrive = useCallback(async () => {
    const redirectTo = new URL('/auth/callback', import.meta.env['VITE_APP_URL']).href;
    const { error } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        scopes: GOOGLE_API_SCOPES,
        // access_type=offline is what makes Google issue a refresh token at
        // all; prompt=consent is what makes it do so again for somebody who
        // already consented in the past.
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) {
      setAccess({ status: 'denied', reason: 'unknown_error' });
    }
  }, [client]);

  const signOut = useCallback(async () => {
    await client.auth.signOut();
    // Signing out is an explicit "I am done here". Anything remembered from
    // a shared link bounced off the gate earlier should not fire the next
    // time somebody signs in on this tab.
    forgetDestination();
    sequenceRef.current += 1;
    inFlightTokenRef.current = null;
    approvedTokenRef.current = null;
    lastSeenSessionRef.current = null;
    setAccess({ status: 'signed_out' });
  }, [client]);

  const value = useMemo(
    () => ({ access, client, signInWithGoogle, reconnectDrive, signOut }),
    [access, client, signInWithGoogle, reconnectDrive, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }
  return context;
}
